import React from 'react';
import { Link } from '@tanstack/react-router';
import { useAuth } from '@workos-inc/authkit-react';
import { usePostHog } from 'posthog-js/react';
import { AnimatedLogo } from '@/components/animated-logo';
import { ModeToggle } from '@/components/mode-toggle';
import { Button } from '@/components/ui/button';
import { getAttributionProps } from '@/lib/attribution';
import { MARKETING_EVENTS } from '@/lib/analytics';
import { CtaButton } from './cta-button';

const NAV_LINKS = [
  { label: 'How it works', href: '#how-it-works' },
  { label: 'Coverage', href: '#coverage' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'FAQ', href: '#faq' },
];

function scrollToSection(e: React.MouseEvent<HTMLAnchorElement>, href: string) {
  const id = href.slice(1);
  const el = document.getElementById(id);
  if (el) {
    e.preventDefault();
    el.scrollIntoView({ behavior: 'smooth' });
  }
}

/**
 * N6 Newspaper masthead — in-flow (not fixed), broadsheet rhythm:
 * standing line + wordmark + actions, hairline, then a centred link row.
 *
 * Mobile: the link row is a single non-wrapping strip that scrolls inside its
 * own container. Wrapping it to two rows cost ~60px of fold on a 375px screen,
 * which is expensive when most traffic is phones. The strip clips at the page
 * edge rather than centring, so it reads as scrollable.
 */
export function LandingHeader() {
  const posthog = usePostHog();
  const { user } = useAuth();

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    link: (typeof NAV_LINKS)[number],
  ) => {
    posthog.capture(MARKETING_EVENTS.navClicked, {
      label: link.label,
      href: link.href,
      location: 'landing_header',
      ...getAttributionProps(),
    });
    scrollToSection(e, link.href);
  };

  return (
    <header className="border-b border-border">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="flex items-center justify-between gap-3 py-3 sm:py-5 md:grid md:grid-cols-[1fr_auto_1fr]">
          <p className="hidden font-display text-xs text-foreground/60 md:block">
            Telangana · Karnataka · Andhra Pradesh
          </p>
          <Link
            to="/"
            aria-label="Boundr home"
            className="shrink-0 md:col-start-2 md:justify-self-center"
          >
            <AnimatedLogo />
          </Link>
          <div className="flex shrink-0 items-center gap-2 md:col-start-3 md:justify-self-end">
            <ModeToggle />
            {user ? (
              <Button asChild size="sm" className="h-10 px-4 sm:px-5">
                <Link to="/dashboard">Show Dashboard</Link>
              </Button>
            ) : (
              <CtaButton location="header" size="sm" className="h-10 px-4 sm:px-5">
                Start Monitoring
              </CtaButton>
            )}
          </div>
        </div>

        <nav className="-mx-4 border-t border-border sm:-mx-6">
          <ul className="flex snap-x items-center gap-x-6 overflow-x-auto px-4 [scrollbar-width:none] sm:gap-x-7 sm:px-6 md:justify-center [&::-webkit-scrollbar]:hidden">
            {NAV_LINKS.map((link) => (
              <li key={link.href} className="snap-start">
                <a
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link)}
                  className="inline-flex min-h-11 items-center whitespace-nowrap font-display text-sm text-foreground/60 underline-offset-4 transition-colors hover:text-ring hover:underline focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </header>
  );
}
